import type { SharedValue } from "react-native-reanimated";
import { useAnimatedStyle } from "react-native-reanimated";
import { StyleSheet } from "react-native-unistyles";

import useAnimatedTheme from "$src/core/hooks/use-animated-theme";
import CustomPlatform from "$src/core/utils/platform/platform";

const stylesheet = StyleSheet.create(({ spacing, colors, border }) => ({
  header: {
    marginBottom: spacing(6),
  },
  container: {
    width: "100%",
    height: "100%",
    gap: spacing(8),
    flexDirection: "row",
  },
  seasonSelector: {
    gap: spacing(2),
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    height: spacing(12),
    paddingHorizontal: spacing(5),
    backgroundColor: colors.backgroundSurface,
    borderRadius: border.radius.md,
  },
  seasons: {
    flex: 1,
    maxWidth: spacing(96),
  },
  season: ({ isSelected }: { isSelected: boolean }) => ({
    outlineColor: isSelected ? colors.borderFocus : colors.borderDefault,
    outlineWidth: isSelected ? border.widths.md : 0,
    outlineOffset: isSelected ? -border.widths.md : 0,
  }),
  episodes: {
    flex: 2,
    gap: spacing(6),
    marginRight: {
      xs: spacing(0),
      // Android TV lands on `lg`, not the `xl` tier tvOS resolves.
      lg: CustomPlatform.select({ tv: spacing(12), default: spacing(82) }),
      xl: CustomPlatform.select({
        tv: spacing(12),
        web: spacing(62),
      }),
    },
    marginLeft: {
      xs: spacing(0),
      lg: spacing(0),
    },
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.borderDefault,
    marginHorizontal: spacing(4),
    marginVertical: spacing(2),
  },
  list: {
    gap: spacing(2),
    paddingHorizontal: spacing(2),
  },
  icon: {
    color: colors.foregroundDefault,
    width: spacing(6),
    height: spacing(6),
  },
  episodeCard: {
    outlineWidth: border.widths.md,
    outlineColor: "transparent",
    outlineOffset: spacing(2),
    borderRadius: border.radius.md,
  },
}));

export const useAnimatedStyles = ({
  isFocused,
}: {
  isFocused: SharedValue<boolean>;
}) => {
  const animatedTheme = useAnimatedTheme();

  const episodeCardStyle = useAnimatedStyle(() => ({
    outlineColor: isFocused.value
      ? animatedTheme.value.colors.borderFocus
      : "transparent",
  }));

  return {
    episodeCardStyle,
  };
};

export default stylesheet;
